import { DateTime } from "luxon";
import { ApolloError } from "@apollo/client";
import {
  PartialVisitFieldsFragment,
  RoleType,
  useVisitsByCaregiverIdQuery,
  useVisitsByContactIdQuery,
  ViewerQuery,
} from "../../api/generated/graphql";
import { useCareContext } from "../../providers";
import { match } from "ts-pattern";
import { useSortingOptions } from "@frontend/lyng/utils/hooks/useSortingOptions";
import { Status, ScheduleVisit } from "../../types";
import { getImportantInformationFromCarePlan } from "../../utils/visits";
import { useFeatureFlag } from "../../providers/FeatureFlags";

export type GetVisitsResult = {
  groupedVisits: GroupedVisits;
  error?: ApolloError;
  loading?: boolean;
};

export type GroupedVisits = {
  // Date is unix timestamp
  [date: number]: ScheduleVisit[];
};

const getStatus = (
  visit: PartialVisitFieldsFragment,
  viewer: ViewerQuery["viewer"] | undefined,
): Status => {
  if (!viewer) {
    return "pending";
  }

  const visitor = visit.visitorsV2.find((visitor) => visitor.id === viewer.id);
  if (!visitor) {
    return "pending";
  }

  if (visitor.clockInTime === null) {
    return "pending";
  }
  if (visitor.clockOutTime === null) {
    return "clockedIn";
  }
  return "clockedOut";
};

export const useGetVisits = (): GetVisitsResult => {
  const { viewer } = useCareContext().state;
  const { nameOrderFn } = useSortingOptions(viewer?.tenantSettings);
  const ff_homes = useFeatureFlag("Homes");

  const isCaregiver = viewer?.roles.some(
    ({ roleType }) => roleType === RoleType.Caregiver,
  );
  const isContact = viewer?.roles.some(
    ({ roleType }) => roleType === RoleType.Contact,
  );

  const from = DateTime.now().startOf("day").minus({ days: 2 }).toISO();
  if (!from) {
    throw new Error("error formatting from date");
  }

  const to = DateTime.now().endOf("day").plus({ days: 40 }).toISO();
  if (!to) {
    throw new Error("error formatting to date");
  }

  const byCaregiver = useVisitsByCaregiverIdQuery({
    skip: !isCaregiver,
    fetchPolicy: "no-cache",
    variables: {
      caregiverId: viewer?.id ?? "",
      from,
      to,
    },
  });

  const byContact = useVisitsByContactIdQuery({
    skip: !isContact,
    fetchPolicy: "no-cache",
    variables: {
      contactId: viewer?.id ?? "",
      from,
      to,
    },
  });

  const accVisits = [...(byCaregiver.data?.visitsByCaregiverId || [])];
  byContact.data?.visitsByContactId.forEach((visit) => {
    if (!accVisits?.find((v) => v.id === visit.id)) {
      accVisits?.push(visit);
    }
  });

  const groupedVisits = accVisits.reduce((group, visit) => {
    const visitStart = DateTime.fromISO(visit.start);
    const date = visitStart.startOf("day");

    if (!ff_homes && visit.subject.__typename === "CareHome") {
      return group;
    }

    if (visitStart < DateTime.local().minus({ hours: 24 })) {
      return group;
    }

    const readOnly =
      !visit.visitorsV2.some((v) => v.id === viewer?.id) &&
      viewer?.roles.some(
        ({ roleType, resourceId }) =>
          roleType === RoleType.Contact && resourceId === visit.subject.id,
      );

    const name = match(visit.subject)
      .with({ __typename: "CareRecipient" }, (careRecipient) =>
        nameOrderFn({
          ...careRecipient,
        }),
      )
      .with({ __typename: "CareHome" }, (careHome) => careHome.name)
      .exhaustive();

    const importantInformation =
      visit.subject.__typename === "CareRecipient"
        ? getImportantInformationFromCarePlan(visit.subject.carePlan)
        : null;

    const key = date.toMillis();
    group[key] = group[key] || [];
    group[key].push({
      id: visit.id,
      from: DateTime.fromISO(visit.start),
      to: DateTime.fromISO(visit.start).plus({
        minutes: visit.durationMinutes,
      }),
      clockIn: visit.clockInTime ? DateTime.fromISO(visit.clockInTime) : null,
      clockOut: visit.clockOutTime
        ? DateTime.fromISO(visit.clockOutTime)
        : null,
      name,
      visitors: visit.visitorsV2,
      subject: visit.subject,
      status: getStatus(visit, viewer),
      cancelledAt: visit.cancelledAt,
      readOnly,
      importantInformation,
    });
    return group;
  }, {} as GroupedVisits);

  return {
    groupedVisits,
    error: byCaregiver.error || byContact.error,
    loading: byCaregiver.loading || byContact.loading,
  };
};
